import { useState, useMemo, useEffect, useRef } from 'react';
import type { MapLocation } from './Map/Map';

interface UseNearbyLocationsReturn {
    nearbyLocations: MapLocation[];
    nearbyLocationIds: string[];
    setNearbyLocationIds: React.Dispatch<React.SetStateAction<string[]>>;
    defaultNearbyIds: string[];
}

/**
 * Custom hook to manage nearby locations state and sync with parent component.
 * Handles filtering locations by state, syncing rating updates from parent,
 * and notifying parent of nearby location changes.
 */
export function useNearbyLocations(
    allLocations: MapLocation[],
    currentStateName?: string,
    onNearbyLocationsChange?: (locations: MapLocation[]) => void
): UseNearbyLocationsReturn {
    // Calculate initial IDs based on state filter
    const defaultNearbyIds = useMemo(() => {
        const filtered = currentStateName
            ? allLocations.filter((location) => location.address.line2.includes(currentStateName))
            : allLocations;
        return filtered.map((loc) => loc.id);
    }, [allLocations, currentStateName]);

    const [nearbyLocationIds, setNearbyLocationIds] = useState<string[]>(defaultNearbyIds);

    // Derive nearby locations from IDs
    const nearbyLocations = useMemo(
        () => allLocations.filter((loc) => nearbyLocationIds.includes(loc.id)),
        [allLocations, nearbyLocationIds]
    );

    // Use a ref to avoid adding callback to dependencies
    const onChangeRef = useRef(onNearbyLocationsChange);
    useEffect(() => {
        onChangeRef.current = onNearbyLocationsChange;
    }, [onNearbyLocationsChange]);

    // Notify parent when nearby locations change
    useEffect(() => {
        if (nearbyLocations.length > 0 && onChangeRef.current) {
            console.log('[useNearbyLocations] Nearby locations updated:', {
                count: nearbyLocations.length,
                firstThree: nearbyLocations.slice(0, 3).map((l) => l.salesforceName),
            });
            onChangeRef.current(nearbyLocations);
        }
    }, [nearbyLocations]);

    return {
        nearbyLocations,
        nearbyLocationIds,
        setNearbyLocationIds,
        defaultNearbyIds,
    };
}
